/**
 * 定型文マスタ (PhraseTemplateMaster) の種別定義 (Issue #449 / SBI-05〜08)。
 *
 * 放送文面の「基本文」を言語別に管理するための **種別 (template_id) の一覧** と、
 * 各種別で使えるプレースホルダを保持する。定型文マスタ編集ページ (SBI-07) が
 * 本定義を参照する。
 *
 * ℹ️ Issue #524: 以前はチャイム音マスタ画面にも多言語文面を併記していた (SBI-08) が、
 *    音源管理画面に SSML が並んで見づらいという指摘を受け撤去した。
 *    多言語文面の参照 / 編集は定型文マスタ画面に一元化している。
 *
 * ⚠️ ここは `lambda/common/ssml_builder.py` の `PHRASE_TEMPLATE_DEFINITIONS` と
 *    **同じ内容に保つこと**。乖離すると「管理画面で編集できるのに放送に反映されない」
 *    (またはその逆) という気づきにくい不整合になる。
 *    正源は Python 側 (`PHRASE_TEMPLATE_*` 定数) で、放送生成が実際に参照するのは
 *    Python 側の template_id である。
 */

import type { DelayBroadcastGranularity } from '../api/types';

import {
  BASE_LANGUAGE_KEYS,
  POLLY_LANGUAGE_OPTIONS,
  type PollyLanguageOption,
} from './pollyVoices';

/**
 * 定型文マスタが対応する言語 (基本 4 言語 = 日 / 英 / 中 / 韓)。
 *
 * ⚠️ `lambda/common/ssml_builder.py` の `PHRASE_TEMPLATE_LANGUAGE_KEYS` と
 *    **必ず同じ内容に保つこと**。乖離すると
 *    「画面には入力欄が出るのに API が 400 を返す」(またはその逆) になる。
 *    同期は `tests/phrase_template_sync/` が CI で突き合わせる。
 *
 * なぜ 4 言語に絞るのか (Issue #527 追補 / レビュー指摘):
 *   Polly マスタ (`POLLY_LANGUAGE_OPTIONS`) には任意テキスト放送用に 20 以上の
 *   言語が登録されているが、それを全部並べると 23 種別 × 20 言語 = 460 個の
 *   入力欄になり、**編集すべき 4 言語が埋もれて事故につながる**。
 *   遅延放送の定型文は受け入れ条件どおり日英中韓で閉じている。
 */
export const PHRASE_TEMPLATE_LANGUAGE_KEYS: readonly string[] = BASE_LANGUAGE_KEYS;

/**
 * 定型文マスタの編集画面に並べる言語の選択肢 (表示順 = 放送順序)。
 *
 * `POLLY_LANGUAGE_OPTIONS` (話者マスタ) から基本 4 言語だけを抜き出す。
 * ラベル (「日本語」等) を二重管理しないため、言語名は話者マスタを正源とする。
 */
export const PHRASE_TEMPLATE_LANGUAGE_OPTIONS: readonly PollyLanguageOption[] =
  PHRASE_TEMPLATE_LANGUAGE_KEYS.map((key) =>
    POLLY_LANGUAGE_OPTIONS.find((language) => language.key === key),
  ).filter((language): language is PollyLanguageOption => language !== undefined);

/** 定型文の種別 1 件ぶんの定義 */
export interface PhraseTemplateDefinition {
  /** 定型文の種別キー (PhraseTemplateMaster の PK / Python 側と一致させる) */
  templateId: string;
  /** 管理画面での表示名 */
  label: string;
  /** 当該定型文で展開されるプレースホルダ (`{...}` の中身) */
  placeholders: readonly string[];
  /** 何の放送で使われるかの説明 (画面のヘルプ表示用) */
  description: string;
  /**
   * 管理画面の左ペインでまとめるグループ名 (Issue #527)。
   *
   * 粒度別デフォルト文面や接続線の発車見込み案内の追加で種別数が増えたため、
   * フラットに並べると探しづらい。グループ見出しでまとめて表示する。
   */
  group: string;
}

/**
 * 放送粒度 → 定型文マスタ `template_id` の接尾辞 (スラッグ)。
 *
 * ⚠️ `lambda/common/ssml_builder.py` の `GRANULARITY_TEMPLATE_SLUG` と
 *    **必ず同じ内容に保つこと** (`phraseTemplates.test.ts` が突き合わせる)。
 */
export const GRANULARITY_TEMPLATE_SLUG: Record<DelayBroadcastGranularity, string> = {
  '1分単位': '1min',
  '5分単位': '5min',
  '10分単位': '10min',
  大幅な遅延: 'significant',
};

/** 粒度別デフォルト文面の `template_id` を組み立てる (Python 側と同じ規則)。 */
export function granularityPhraseTemplateId(
  prefix: string,
  granularity: DelayBroadcastGranularity,
): string {
  return `${prefix}_${GRANULARITY_TEMPLATE_SLUG[granularity] ?? GRANULARITY_TEMPLATE_SLUG['大幅な遅延']}`;
}

/** 自列車遅延の定型文 ID (`lambda/common/ssml_builder.py` と一致) */
export const PHRASE_TEMPLATE_SELF_TRAIN_DELAY = 'self_train_delay';
/** 接続線遅延の定型文 ID */
export const PHRASE_TEMPLATE_CONNECTING_DELAY = 'connecting_delay';
/** 運行情報の定型文 ID */
export const PHRASE_TEMPLATE_TRAIN_INFO = 'train_info';
/** 接続線遅延 (乗換駅) の導入文の定型文 ID */
export const PHRASE_TEMPLATE_CONNECTING_STATION_INTRO = 'connecting_station_intro';
/** 接続線遅延 (乗換駅) の路線文の定型文 ID */
export const PHRASE_TEMPLATE_CONNECTING_STATION_LINE = 'connecting_station_line';
/** 接続線遅延 (乗換駅) の上限超過時の案内文の定型文 ID */
export const PHRASE_TEMPLATE_CONNECTING_STATION_OVER_LIMIT = 'connecting_station_over_limit';

// --- Issue #527: 粒度別デフォルト文面 (放送地点の SSML 欄が空欄のときに使われる文面) ---
/** 自列車遅延 (粒度別) の template_id 接頭辞 */
export const PHRASE_TEMPLATE_SELF_TRAIN_DELAY_PREFIX = 'self_train_delay';
/** 接続線遅延 transfer_aware (粒度別) の template_id 接頭辞 */
export const PHRASE_TEMPLATE_CONNECTING_DELAY_PREFIX = 'connecting_delay';
/** 接続線遅延 (乗換駅) の方面文 (粒度別) の template_id 接頭辞 */
export const PHRASE_TEMPLATE_CONNECTING_STATION_DIRECTION_PREFIX = 'connecting_station_direction';
/** 接続線遅延 (乗換駅・whole_line) の方面文 (粒度別) の template_id 接頭辞 */
export const PHRASE_TEMPLATE_CONNECTING_STATION_WHOLE_LINE_PREFIX = 'connecting_station_whole_line';
/** 接続線遅延 (接続線全体の遅延 / 単一テンプレート) の定型文 ID */
export const PHRASE_TEMPLATE_CONNECTING_DELAY_WHOLE_LINE = 'connecting_delay_whole_line';
/** 接続線遅延 (折り返し列車の遅延（判定区間指定） / 単一テンプレート) の定型文 ID */
export const PHRASE_TEMPLATE_CONNECTING_DELAY_SECTION_SCHEDULED =
  'connecting_delay_section_scheduled';
/** 接続線遅延 (接続線の発車見込み案内 / 単一テンプレート) の定型文 ID */
export const PHRASE_TEMPLATE_CONNECTING_DELAY_DEPARTURE_FORECAST =
  'connecting_delay_departure_forecast';
/** 接続線遅延 (乗換駅・発車見込み案内) の路線文の定型文 ID (語尾「発車の見込みです」を担う) */
export const PHRASE_TEMPLATE_CONNECTING_STATION_DEPARTURE_FORECAST_LINE =
  'connecting_station_departure_forecast_line';
/** 接続線遅延 (乗換駅・発車見込み案内) の方面文の定型文 ID (時刻まで。語尾は持たない) */
export const PHRASE_TEMPLATE_CONNECTING_STATION_DEPARTURE_FORECAST =
  'connecting_station_departure_forecast';

/** 放送粒度の表示順 (`DELAY_BROADCAST_GRANULARITIES` と同じ並び) */
const GRANULARITY_ORDER: readonly DelayBroadcastGranularity[] = [
  '1分単位',
  '5分単位',
  '10分単位',
  '大幅な遅延',
];

/** 粒度別定型文で共通に使えるプレースホルダ (Python 側 `_GRANULARITY_DELAY_PLACEHOLDERS`)。 */
const GRANULARITY_DELAY_PLACEHOLDERS: readonly string[] = [
  'delay_minutes_1',
  'delay_minutes_5',
  'delay_minutes_10',
  'delay_minutes',
  'delay_seconds',
  'delay',
];

/**
 * 粒度別 (1分/5分/10分/大幅) の定型文定義 4 件を生成する。
 *
 * Python 側 `_granularity_phrase_template_definitions()` と同じ規則で機械生成し、
 * 手書きによる漏れ (粒度の増減時) を防ぐ。
 */
function granularityDefinitions(
  prefix: string,
  label: string,
  group: string,
  placeholders: readonly string[],
  description: string,
): PhraseTemplateDefinition[] {
  return GRANULARITY_ORDER.map((granularity) => ({
    templateId: granularityPhraseTemplateId(prefix, granularity),
    label: `${label} (${granularity})`,
    placeholders,
    description: `${description} (放送粒度: ${granularity})`,
    group,
  }));
}

/** 管理画面の左ペインのグループ名 (Issue #527) */
const GROUP_SELF_TRAIN_DELAY = '自列車遅延';
const GROUP_CONNECTING_DELAY = '接続線遅延';
const GROUP_CONNECTING_STATION = '接続線遅延 (乗換駅)';
const GROUP_TRAIN_INFO = '運行情報';

/**
 * 定型文の種別一覧 (管理画面の表示順)。
 *
 * 受け入れ条件「自列車遅延／接続線遅延は、日本語・英語・中国語・韓国語で
 * 放送されること」に対応する 2 種別を先頭に置く。
 *
 * ⚠️ Issue #527: `lambda/common/ssml_builder.py` の `PHRASE_TEMPLATE_DEFINITIONS` と
 *    **template_id の集合を一致させること** (`phraseTemplates.test.ts` が検査する)。
 */
export const PHRASE_TEMPLATE_DEFINITIONS: readonly PhraseTemplateDefinition[] = [
  {
    templateId: PHRASE_TEMPLATE_SELF_TRAIN_DELAY,
    label: '自列車遅延',
    // ℹ️ SBI-3: Lambda 側のコード内既定文面を分単位 (切り捨て) 化したため
    //    `delay_minutes_1/5/10` を許可リストへ追記した。追記しないと
    //    **既定文面をそのまま管理画面から保存できない** (API が 400 を返す)。
    placeholders: [
      'delay_minutes_1',
      'delay_minutes_5',
      'delay_minutes_10',
      'delay',
      'delay_seconds',
      'delay_minutes',
      'route_id',
      'route_name',
      'route',
    ],
    description: 'ご乗車中の列車が遅れている場合の基本文。',
    group: GROUP_SELF_TRAIN_DELAY,
  },
  ...granularityDefinitions(
    PHRASE_TEMPLATE_SELF_TRAIN_DELAY_PREFIX,
    '自列車遅延',
    GROUP_SELF_TRAIN_DELAY,
    [...GRANULARITY_DELAY_PLACEHOLDERS, 'route_id', 'route_name', 'route'],
    '自列車遅延で放送パターンの文面が未入力のときに使われる文面。',
  ),
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_DELAY,
    label: '接続線遅延',
    // Issue #527 追補: コード内既定文面が `{from_prefix}` を使うため許可リストへ追記。
    placeholders: [
      'route_id',
      'route_name',
      'route',
      'from_station',
      'from_prefix',
      'direction_name',
      // ℹ️ SBI-3: コード内既定文面が `{delay_minutes_1}` (切り捨て) を使うため必須。
      'delay_minutes_1',
      'delay_minutes_5',
      'delay_minutes_10',
      'delay_minutes',
      'delay_seconds',
    ],
    description: '乗り換え先の路線が遅れている場合の基本文。',
    group: GROUP_CONNECTING_DELAY,
  },
  ...granularityDefinitions(
    PHRASE_TEMPLATE_CONNECTING_DELAY_PREFIX,
    '接続線遅延',
    GROUP_CONNECTING_DELAY,
    [
      ...GRANULARITY_DELAY_PLACEHOLDERS,
      'route_id',
      'route_name',
      'route',
      'from_station',
      'from_prefix',
      'direction_name',
      'direction_suffix',
    ],
    '接続線遅延 (乗り換え列車の遅延) で放送パターンの文面が未入力のときに使われる文面。',
  ),
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_DELAY_WHOLE_LINE,
    label: '接続線遅延 (接続線全体の遅延)',
    // Issue #527 追補: 粒度別の切り捨て分数もレンダラが展開するため許可する。
    placeholders: [
      'route_id',
      'route_name',
      'route',
      'delay_minutes_1',
      'delay_minutes_5',
      'delay_minutes_10',
      'delay_minutes',
      'delay_seconds',
    ],
    description:
      '接続線遅延「接続線全体の遅延」モードで SSML テンプレートが未入力のときに使われる文面。',
    group: GROUP_CONNECTING_DELAY,
  },
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_DELAY_SECTION_SCHEDULED,
    label: '接続線遅延 (折り返し列車の遅延（判定区間指定）)',
    placeholders: [
      'route_id',
      'route_name',
      'route',
      'from_station',
      'from_prefix',
      'direction_name',
      // コード内既定文面が `{delay_minutes_1}` を使うため必須 (Issue #527 追補)。
      'delay_minutes_1',
      'delay_minutes_5',
      'delay_minutes_10',
      'delay_minutes',
      'delay_seconds',
    ],
    description:
      '接続線遅延「折り返し列車の遅延（判定区間指定）」モードで SSML テンプレートが未入力のときに使われる文面。',
    group: GROUP_CONNECTING_DELAY,
  },
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_DELAY_DEPARTURE_FORECAST,
    label: '接続線遅延 (接続線の発車見込み案内)',
    // Python 側 PHRASE_TEMPLATE_DEFINITIONS と同じ許可リスト。日本語の既定文面が
    // `{from_prefix}` / `{headsign}`、外国語の既定文面が `{from_station}` / `{headsign}` を
    // 使うため、いずれも必須。遅延分数は読み上げないので許可しない。
    placeholders: [
      'departure_time',
      'departure_hour',
      'departure_minute',
      'headsign',
      'direction_name',
      'route_id',
      'route_name',
      'route',
      'from_station',
      'from_prefix',
    ],
    description:
      '接続線遅延「接続線の発車見込み案内」モードで SSML テンプレートが未入力のときに使われる文面。',
    group: GROUP_CONNECTING_DELAY,
  },
  {
    templateId: PHRASE_TEMPLATE_TRAIN_INFO,
    label: '運行情報',
    placeholders: ['train_info_text'],
    description: '運行情報 (ServiceAlert) を読み上げる際の基本文。',
    group: GROUP_TRAIN_INFO,
  },
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_STATION_INTRO,
    label: '接続線遅延 (乗換駅) の導入文',
    placeholders: ['transfer_station'],
    description: '乗換駅ごとにまとめて案内する際の冒頭文。',
    group: GROUP_CONNECTING_STATION,
  },
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_STATION_LINE,
    label: '接続線遅延 (乗換駅) の路線文',
    placeholders: ['route_name', 'directions_text'],
    description: '乗換駅の案内内で路線ごとに読み上げる文。',
    group: GROUP_CONNECTING_STATION,
  },
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_STATION_DEPARTURE_FORECAST_LINE,
    label: '接続線遅延 (乗換駅・発車見込み案内) の路線文',
    placeholders: ['route_name', 'directions_text'],
    description:
      '乗換駅の「接続線の発車見込み案内」で路線ごとに読み上げる文。文末の「発車の見込みです」はこの文が担う。',
    group: GROUP_CONNECTING_STATION,
  },
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_STATION_DEPARTURE_FORECAST,
    label: '接続線遅延 (乗換駅・発車見込み案内) の方面文',
    // Python 側 PHRASE_TEMPLATE_DEFINITIONS と同じ許可リスト。乗換駅は part2 の流儀で
    // `transfer_station`。遅延分数は読み上げないので許可しない。
    placeholders: [
      'departure_time',
      'departure_hour',
      'departure_minute',
      'headsign',
      'direction_name',
      'route_id',
      'route_name',
      'route',
      'transfer_station',
    ],
    description:
      '乗換駅の「接続線の発車見込み案内」で方面ごとに読み上げる部分文。時刻までを書き、語尾は書かない (路線文が閉じる)。',
    group: GROUP_CONNECTING_STATION,
  },
  ...granularityDefinitions(
    PHRASE_TEMPLATE_CONNECTING_STATION_DIRECTION_PREFIX,
    '接続線遅延 (乗換駅) の方面文',
    GROUP_CONNECTING_STATION,
    [
      ...GRANULARITY_DELAY_PLACEHOLDERS,
      'direction_name',
      'route_id',
      'route_name',
      'route',
      'transfer_station',
    ],
    '乗換駅別の方面部分文が未入力のときに使われる文面。',
  ),
  ...granularityDefinitions(
    PHRASE_TEMPLATE_CONNECTING_STATION_WHOLE_LINE_PREFIX,
    '接続線遅延 (乗換駅・接続線全体) の方面文',
    GROUP_CONNECTING_STATION,
    [...GRANULARITY_DELAY_PLACEHOLDERS, 'route_id', 'route_name', 'route', 'transfer_station'],
    '乗換駅別 (接続線全体の遅延モード) の方面部分文が未入力のときに使われる文面。',
  ),
  {
    templateId: PHRASE_TEMPLATE_CONNECTING_STATION_OVER_LIMIT,
    label: '接続線遅延 (乗換駅) の上限超過時の案内文',
    placeholders: ['transfer_station'],
    description:
      '放送する接続線数が上限を超えたときに、各路線の遅延案内文の代わりに読み上げる固定文。',
    group: GROUP_CONNECTING_STATION,
  },
];

/**
 * 定型文の種別をグループ単位にまとめて返す (管理画面の左ペイン用 / Issue #527)。
 *
 * `PHRASE_TEMPLATE_DEFINITIONS` の並び順を保ったままグループ化するため、
 * 表示順は定義順と一致する。種別が増えてフラットに並べると探しづらくなったため、
 * グループ見出し (自列車遅延 / 接続線遅延 / 乗換駅 / 運行情報) でまとめる。
 */
export function groupedPhraseTemplateDefinitions(): {
  group: string;
  definitions: PhraseTemplateDefinition[];
}[] {
  const groups: { group: string; definitions: PhraseTemplateDefinition[] }[] = [];
  for (const definition of PHRASE_TEMPLATE_DEFINITIONS) {
    const existing = groups.find((g) => g.group === definition.group);
    if (existing) {
      existing.definitions.push(definition);
    } else {
      groups.push({ group: definition.group, definitions: [definition] });
    }
  }
  return groups;
}

/** `templateId` から定義を引く (未知の ID は `undefined`)。 */
export function findPhraseTemplateDefinition(
  templateId: string,
): PhraseTemplateDefinition | undefined {
  return PHRASE_TEMPLATE_DEFINITIONS.find((d) => d.templateId === templateId);
}

/** `templateId` の表示名を返す (未知の ID は ID 自体を返す)。 */
export function phraseTemplateLabel(templateId: string): string {
  return findPhraseTemplateDefinition(templateId)?.label ?? templateId;
}

/**
 * プレースホルダを画面表示用の文字列 (`{a} / {b}`) に整形する。
 *
 * 定型文を編集する運用者が「何を差し込めるか」を一目で分かるようにするため、
 * 入力欄の近くにこの文字列を表示する。
 */
export function formatPlaceholders(placeholders: readonly string[]): string {
  return placeholders.map((p) => `{${p}}`).join(' / ');
}
